import { useQuery } from '@apollo/client';
import { endOfDay, startOfDay, startOfWeek } from 'date-fns';
import { useMemo } from 'react';

import { GET_RECRUITER_LEADERBOARD_QUERY } from '../graphql/queries';

export type LeaderboardPeriod = 'today' | 'thisWeek' | 'history';

export interface IRecruiterLeaderboardEntry {
  userId: string;
  firstName: string;
  lastName: string;
  regionId?: string;
  newDonorCount: number;
}

export function useRecruiterLeaderboard(period: LeaderboardPeriod) {
  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === 'today') {
      return {
        start: startOfDay(now),
        end: endOfDay(now),
      };
    }
    // thisWeek — Monday to end of today
    return {
      start: startOfWeek(now, { weekStartsOn: 1 }),
      end: endOfDay(now),
    };
  }, [period]);

  const { data, loading, error } = useQuery(GET_RECRUITER_LEADERBOARD_QUERY, {
    variables: {
      input: {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      },
    },
    skip: period === 'history',
  });

  const entries: IRecruiterLeaderboardEntry[] = useMemo(
    () =>
      (data?.getRecruiterLeaderboard?.entries ?? []).map(
        (e: {
          userId: string;
          firstName: string;
          lastName: string;
          regionId?: string;
          newDonorCount: number;
        }) => ({
          userId: e.userId,
          firstName: e.firstName,
          lastName: e.lastName,
          regionId: e.regionId,
          newDonorCount: e.newDonorCount,
        })
      ),
    [data]
  );

  return { entries, loading, error };
}
